import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { PageMeta, PageHero, Section, SectionHeading, FeatureCard, CtaBlock, Pill } from "@/components/PageShellParts";
import { AlertTriangle, Eye, HeartPulse, Lock, Scale, ShieldCheck, UserCheck, Users } from "lucide-react";

const Icons = { AlertTriangle, Eye, HeartPulse, Lock, Scale, ShieldCheck, UserCheck, Users };

export default function TrustSafetyPage() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <PageMeta title="Trust, Safety & Responsible AI" description="Learn how PsyTech approaches responsible AI, human oversight, safety boundaries, and privacy-aware mental health assessment support." />
      <div className="pt-20">
      <PageHero
        eyebrow="Trust & Safety"
        title="Trust, Safety & Responsible AI"
        subtitle="PsyTech is designed to support structured mental health assessment workflows with privacy, transparency, and human oversight at the center."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="cyan">Human-in-the-loop</Pill>
          <Pill tone="cyan">Privacy-by-design</Pill>
          <Pill tone="cyan">Professional review encouraged</Pill>
        </div>
      </PageHero>

      <Section>
        <SectionHeading
          eyebrow="Responsible AI"
          title="AI that supports, not replaces, qualified professionals"
          subtitle="We build PsyTech as decision support for licensed professionals — not as a final authority on clinical questions."
        />
        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard icon={Icons.ShieldCheck} title="Decision support, not final authority">
            AI-generated summaries and patterns are intended to help professionals review information faster. They are starting points for professional interpretation, not clinical conclusions.
          </FeatureCard>
          <FeatureCard icon={Icons.UserCheck} title="Transparent limitations">
            We are explicit about what PsyTech can and cannot do. The platform is not a medical device and is not a substitute for qualified clinical judgement.
          </FeatureCard>
          <FeatureCard icon={Icons.Eye} title="Reviewable outputs">
            Every AI-assisted output is designed to be inspected, questioned, and overridden by the professional using it.
          </FeatureCard>
          <FeatureCard icon={Icons.Scale} title="Ethical boundaries in the product">
            The system is built to refuse diagnosis, medication advice, and emergency guidance by design, and to redirect users to qualified support when appropriate.
          </FeatureCard>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading
          eyebrow="Human-in-the-loop"
          title="Professionals remain responsible for interpretation and decisions"
          subtitle="PsyTech can help organize responses, surface patterns, and create structured summaries. Clinical judgement stays with qualified professionals."
        />
        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.Users} title="Structured organization">
            Responses are grouped into clear, professional formats that make review faster — without making decisions for the reviewer.
          </FeatureCard>
          <FeatureCard icon={Icons.Eye} title="Pattern surfacing">
            PsyTech highlights relevant themes and consistency across responses so the professional can investigate further.
          </FeatureCard>
          <FeatureCard icon={Icons.UserCheck} title="Professional sign-off">
            In clinical workflows, every insight is intended to be validated by a qualified professional before it reaches a care decision.
          </FeatureCard>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Privacy-by-design"
          title="Privacy is a product requirement, not a checklist"
        />
        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard icon={Icons.Lock} title="Data minimization">
            We aim to collect only what is necessary for assessment support — and to keep raw identifiers separated from assessment content where possible.
          </FeatureCard>
          <FeatureCard icon={Icons.ShieldCheck} title="Role-based access">
            Access to sensitive information is scoped by role. Administrators, professionals, and support staff only see what they need for their function.
          </FeatureCard>
          <FeatureCard icon={Icons.Lock} title="Secure handling">
            Encrypted connections, secure storage practices, and careful logging are part of how the platform is built.
          </FeatureCard>
          <FeatureCard icon={Icons.Eye} title="Transparent usage">
            We explain in plain language what data is used for and why, so users and partners can make informed decisions.
          </FeatureCard>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading
          eyebrow="Safety boundaries"
          title="What PsyTech is not"
          subtitle="We are explicit about the limits of the product so professionals and users know where the platform should not be relied upon."
        />
        <div className="grid md:grid-cols-2 gap-3">
          {[
            "Not a medical diagnosis tool",
            "Not a replacement for a psychologist, psychiatrist, therapist, or physician",
            "Not a medication advice service",
            "Not a crisis or emergency service",
            "Not a certified medical device",
            "Not a substitute for informed consent and professional oversight",
          ].map((t) => (
            <div
              key={t}
              className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3"
            >
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700">{t}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Bias-aware assessment"
          title="Careful about bias — honest about limits"
          subtitle="PsyTech aims to reduce avoidable bias by supporting structured, consistent, and context-aware assessment workflows. We do not claim bias is fully eliminated."
        />
        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.Scale} title="Structured consistency">
            The same question structure is applied consistently to reduce ad-hoc variation between reviews.
          </FeatureCard>
          <FeatureCard icon={Icons.UserCheck} title="Context-aware review">
            Cultural, linguistic, and situational context matters. The platform is designed to be reviewed — not automated — for sensitive decisions.
          </FeatureCard>
          <FeatureCard icon={Icons.Eye} title="Ongoing evaluation">
            Bias mitigation is an ongoing discipline. We treat it as a continuous responsibility, not a solved problem.
          </FeatureCard>
        </div>
      </Section>

      <Section className="bg-amber-50/40 border-y border-amber-200">
        <div className="flex items-start gap-4 max-w-3xl mx-auto">
          <HeartPulse className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
              Crisis and urgent support
            </h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              PsyTech is not an emergency service. If someone is in immediate danger or needs urgent mental health support,
              they should contact local emergency services or a qualified professional immediately. In the EU, the
              emergency number is <strong>112</strong>.
            </p>
          </div>
        </div>
      </Section>

      <CtaBlock
        title="Discuss responsible AI use with PsyTech"
        subtitle="If you are evaluating PsyTech for a clinic, institution, or organization, we are happy to walk through how safety and oversight are handled in practice."
        primaryHref="/pilot-program"
        primaryLabel="Explore the pilot program"
        secondaryHref="/#contact"
        secondaryLabel="Contact PsyTech"
        testid="trust-safety-cta"
      />
    </div>
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
            It does not provide diagnosis, treatment, medication advice, or emergency support.
            AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
